/**
 * Mediator - MediatR-style dispatcher for CQRS
 * Dispatches queries and commands to their registered handlers
 */

import type { ICommand, IHandler, IQuery } from './types';
import { logger } from './logger';

/**
 * Mediator class - singleton dispatcher for queries and commands
 */
class Mediator {
  private handlers = new Map<string, IHandler<unknown, unknown>>();

  /**
   * Register a handler for a specific request type
   * @param name - The name of the request type (usually the class name)
   * @param handler - The handler instance
   */
  register<TRequest, TResult>(name: string, handler: IHandler<TRequest, TResult>): void {
    if (this.handlers.has(name)) {
      logger.warn('Mediator', `Handler for "${name}" is being replaced`);
    }
    this.handlers.set(name, handler as IHandler<unknown, unknown>);
  }

  /**
   * Unregister a handler (useful for testing)
   * @param name - The name of the request type
   */
  unregister(name: string): boolean {
    return this.handlers.delete(name);
  }

  /**
   * Clear all registered handlers (useful for testing)
   */
  clear(): void {
    this.handlers.clear();
  }

  /**
   * Check if a handler is registered
   * @param name - The name of the request type
   */
  hasHandler(name: string): boolean {
    return this.handlers.has(name);
  }

  /**
   * Send a query or command to its handler
   * @param request - The query or command instance
   * @returns The result from the handler
   */
  async send<TResult>(request: IQuery<TResult> | ICommand<TResult>): Promise<TResult> {
    const name = request.type;

    if (!name) {
      throw new Error(
        `[Mediator] Request is missing 'type' property. ` +
          `All queries and commands must have a 'type' property for production builds.`
      );
    }

    const handler = this.handlers.get(name);

    if (!handler) {
      throw new Error(
        `[Mediator] No handler registered for "${name}". ` +
          `Make sure to call the setup function before using the mediator.`
      );
    }

    return handler.handle(request) as Promise<TResult>;
  }
}

/**
 * Singleton mediator instance
 */
export const mediator = new Mediator();
